import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownIcon, ArrowUpRightIcon, CheckIcon, ClapperboardIcon, DownloadIcon, ImageUpIcon, MaximizeIcon, PlayIcon, SparklesIcon, WandSparklesIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
const studioUrl = 'https://studio.creceelite.com';
const benefits = [{
  title: 'Imágenes listas para publicar',
  description: 'Sube una imagen, mejórala y descárgala en formatos sociales como 1:1, 6:9 y 9:16.',
  icon: ImageUpIcon
}, {
  title: 'Contenido más profesional',
  description: 'Crea versiones visuales más limpias, elegantes y premium para tu marca personal o ministerio.',
  icon: SparklesIcon
}, {
  title: 'Videos cortos optimizados',
  description: 'Prepara videos para redes sociales con una presentación más profesional y adaptada a plataformas modernas.',
  icon: ClapperboardIcon
}, {
  title: 'Diseñado para marcas con propósito',
  description: 'Una herramienta creada para personas que quieren comunicar con excelencia, claridad y visión.',
  icon: WandSparklesIcon
}];
const formats = ['1:1', '6:9', '9:16'];
export function StudioPromo() {
  const {
    ref,
    isVisible
  } = useScrollReveal({
    threshold: 0.1
  });
  return <section id="studio" aria-labelledby="studio-titulo" className="relative w-full overflow-hidden border-t border-white/5 bg-[#070c14] py-20 md:py-32" data-id="element-196">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gold/40" aria-hidden="true" data-id="element-197" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12" data-id="element-198">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-199">
          <motion.div ref={ref} initial={{
          opacity: 0,
          y: 36
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.9,
          ease: [0.16, 1, 0.3, 1]
        }} className="order-1" data-id="element-200">
            <div className="mb-6 flex items-center gap-4" data-id="element-201">
              <span className="h-px w-8 bg-gold" data-id="element-202" />
              <span className="font-montserrat text-xs font-semibold uppercase tracking-[0.28em] text-gold" data-id="element-203">
                Herramienta creativa
              </span>
            </div>

            <div className="mb-7 flex items-center gap-3" data-id="element-204">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-300/10 text-cyan-200" data-id="element-205">
                <SparklesIcon className="h-5 w-5" aria-hidden="true" data-id="element-206" />
              </div>
              <p className="font-montserrat text-sm font-semibold tracking-wide text-warmWhite" data-id="element-207">
                CreceElite <span className="text-gold" data-id="element-208">Studio</span>
              </p>
            </div>

            <h2 id="studio-titulo" className="max-w-xl font-montserrat text-4xl font-extrabold leading-[1.08] tracking-tight text-warmWhite sm:text-5xl md:text-6xl" data-id="element-209">
              Crea contenido visual premium para{' '}
              <span className="text-gold" data-id="element-210">elevar tu mensaje</span>
            </h2>

            <p className="mt-6 max-w-xl font-montserrat text-base font-light leading-relaxed text-mutedGray md:text-lg" data-id="element-211">
              Transforma tus imágenes y videos en piezas listas para Instagram,
              TikTok, Reels y Stories. Diseñado para marcas con propósito.
            </p>

            <div className="mt-8 flex flex-wrap gap-2" aria-label="Plataformas compatibles" data-id="element-212">
              {['Para Instagram', 'Para TikTok', 'Para Reels', 'Para Stories', 'Contenido con propósito'].map(badge => <span key={badge} className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 font-montserrat text-[10px] font-medium uppercase tracking-[0.12em] text-warmWhite/80" data-id="element-213">
                  {badge}
                </span>)}
            </div>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-214">
              <a href={studioUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-gold px-6 py-3.5 font-montserrat text-sm font-semibold tracking-wide text-richBlack transition-all duration-300 hover:bg-goldLight hover:shadow-[0_0_30px_rgba(201,168,76,0.24)] focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-[#070c14]" data-id="element-215">
                Entrar a CreceElite Studio
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-216" />
              </a>

              <a href="#studio-como-funciona" className="inline-flex min-h-12 items-center justify-center gap-2 rounded-lg border border-white/20 px-6 py-3.5 font-montserrat text-sm font-medium tracking-wide text-warmWhite transition-colors duration-300 hover:border-gold hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-[#070c14]" data-id="element-217">
                Ver cómo funciona
                <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-218" />
              </a>
            </div>

            <p className="mt-7 font-montserrat text-sm italic text-[#e7dfcf]" data-id="element-219">
              “Tu mensaje merece verse con excelencia.”
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 44
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 1,
          delay: 0.16,
          ease: [0.16, 1, 0.3, 1]
        }} className="order-2" data-id="element-220">
            <StudioMockup data-id="element-221" />
          </motion.div>
        </div>

        <div id="studio-como-funciona" className="scroll-mt-24 mt-20 border-t border-white/10 pt-16 md:mt-28 md:pt-20" data-id="element-222">
          <motion.div initial={{
          opacity: 0,
          y: 28
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.24,
          ease: [0.16, 1, 0.3, 1]
        }} className="mx-auto mb-10 max-w-3xl text-center" data-id="element-223">
            <div className="mb-5 flex items-center justify-center gap-4" data-id="element-224">
              <span className="h-px w-8 bg-gold/70" data-id="element-225" />
              <span className="font-montserrat text-xs uppercase tracking-[0.28em] text-gold" data-id="element-226">
                Cómo funciona
              </span>
              <span className="h-px w-8 bg-gold/70" data-id="element-227" />
            </div>
            <p className="font-montserrat text-lg font-light leading-relaxed text-mutedGray md:text-xl" data-id="element-228">
              Una experiencia clara para convertir una idea, una imagen o un
              video en contenido listo para compartir.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-229">
            {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return <motion.article key={benefit.title} initial={{
              opacity: 0,
              y: 28
            }} animate={isVisible ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.75,
              delay: 0.3 + index * 0.1,
              ease: [0.16, 1, 0.3, 1]
            }} className="group rounded-2xl border border-white/10 bg-[#0b1421] p-6 shadow-[0_16px_36px_rgba(0,0,0,0.2)] transition-colors duration-300 hover:border-gold/40" data-id="element-230">
                  <div className="mb-6 flex h-11 w-11 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-richBlack" data-id="element-231">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-232" />
                  </div>
                  <h3 className="mb-3 font-montserrat text-base font-semibold leading-snug text-warmWhite" data-id="element-233">
                    {benefit.title}
                  </h3>
                  <p className="font-montserrat text-sm font-light leading-relaxed text-mutedGray" data-id="element-234">
                    {benefit.description}
                  </p>
                </motion.article>;
          })}
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.72,
          ease: [0.16, 1, 0.3, 1]
        }} className="mt-12 rounded-2xl border border-gold/20 bg-[#0b1421] px-6 py-10 text-center shadow-[0_18px_48px_rgba(0,0,0,0.2)] md:mt-16 md:px-12" data-id="element-235">
            <p className="mb-3 font-montserrat text-xs font-semibold uppercase tracking-[0.26em] text-gold" data-id="element-236">
              CreceElite Studio
            </p>
            <h3 className="font-montserrat text-2xl font-bold text-warmWhite md:text-4xl" data-id="element-237">
              Empieza a crear contenido premium hoy
            </h3>
            <p className="mx-auto mt-4 max-w-2xl font-montserrat text-sm font-light leading-relaxed text-mutedGray md:text-base" data-id="element-238">
              Accede a CreceElite Studio y transforma tus ideas en contenido
              visual listo para compartir.
            </p>
            <a href={studioUrl} target="_blank" rel="noopener noreferrer" className="group mt-7 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-gold px-7 py-3.5 font-montserrat text-sm font-semibold tracking-wide text-richBlack transition-all duration-300 hover:bg-goldLight hover:shadow-[0_0_30px_rgba(201,168,76,0.24)] focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-[#0b1421]" data-id="element-239">
              Abrir Studio
              <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-240" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}
function StudioMockup() {
  return <div className="relative mx-auto max-w-xl" data-id="element-241">
      <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-gold/[0.06] blur-3xl" aria-hidden="true" data-id="element-242" />
      <div className="relative overflow-hidden rounded-2xl border border-white/15 bg-[#101a2b] shadow-[0_26px_80px_rgba(0,0,0,0.44)]" data-id="element-243">
        <div className="flex items-center justify-between border-b border-white/10 bg-[#0c1524] px-4 py-3 sm:px-5" data-id="element-244">
          <div className="flex items-center gap-2" data-id="element-245">
            <span className="h-2 w-2 rounded-full bg-[#dca34c]" data-id="element-246" />
            <span className="h-2 w-2 rounded-full bg-[#8590a3]" data-id="element-247" />
            <span className="h-2 w-2 rounded-full bg-[#8590a3]" data-id="element-248" />
          </div>
          <p className="font-montserrat text-[10px] font-semibold uppercase tracking-[0.2em] text-[#d9e1ef]" data-id="element-249">
            CreceElite Studio
          </p>
          <div className="h-4 w-12 rounded-full bg-white/5" data-id="element-250" />
        </div>

        <div className="grid min-h-[390px] grid-cols-[82px_minmax(0,1fr)] sm:min-h-[450px] sm:grid-cols-[104px_minmax(0,1fr)]" data-id="element-251">
          <aside className="border-r border-white/10 bg-[#0c1524] px-2 py-4 sm:px-3" data-id="element-252">
            <div className="mb-5 flex items-center justify-center" data-id="element-253">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-cyan-300/15 text-cyan-200" data-id="element-254">
                <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-255" />
              </div>
            </div>
            {['Crear', 'Imágenes', 'Videos', 'Proyectos'].map((item, index) => <div key={item} className={`mb-2 rounded-md px-2 py-2 text-center font-montserrat text-[9px] font-medium ${index === 0 ? 'bg-gold/15 text-gold' : 'text-[#9aa8bc]'}`} data-id="element-256">
                {item}
              </div>)}
          </aside>

          <div className="p-4 sm:p-5" data-id="element-257">
            <div className="mb-4 flex items-center justify-between" data-id="element-258">
              <div data-id="element-259">
                <p className="font-montserrat text-[10px] font-medium text-[#9eabc0]" data-id="element-260">
                  Nuevo proyecto
                </p>
                <h3 className="font-montserrat text-sm font-semibold text-[#f5f5f0] sm:text-base" data-id="element-261">
                  Crea para comunicar con propósito
                </h3>
              </div>
              <span className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-2 py-1 font-montserrat text-[8px] font-semibold uppercase tracking-wider text-cyan-200" data-id="element-262">
                Studio
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1.15fr_0.85fr]" data-id="element-263">
              <div className="rounded-xl border border-dashed border-cyan-200/25 bg-[#132037] p-4" data-id="element-264">
                <div className="flex h-28 flex-col items-center justify-center rounded-lg border border-white/10 bg-[#0e1828] text-center sm:h-36" data-id="element-265">
                  <ImageUpIcon className="mb-2 h-6 w-6 text-cyan-200" aria-hidden="true" data-id="element-266" />
                  <p className="font-montserrat text-[10px] font-medium text-[#e7edf7]" data-id="element-267">
                    Subir imagen o video
                  </p>
                  <p className="mt-1 font-montserrat text-[9px] text-[#8f9db2]" data-id="element-268">
                    Arrastra o selecciona un archivo
                  </p>
                </div>
                <div className="mt-3 flex items-center gap-2" data-id="element-269">
                  <span className="h-5 w-5 rounded bg-gold/20" data-id="element-270" />
                  <span className="h-5 w-5 rounded bg-cyan-300/10" data-id="element-271" />
                  <span className="h-5 w-5 rounded bg-white/10" data-id="element-272" />
                  <span className="font-montserrat text-[8px] text-[#8391a7]" data-id="element-273">
                    Tus archivos
                  </span>
                </div>
              </div>

              <div className="space-y-2" data-id="element-274">
                {[{
                label: 'Mejorar calidad',
                icon: SparklesIcon
              }, {
                label: 'Crear variación',
                icon: WandSparklesIcon
              }, {
                label: 'Adaptar para redes',
                icon: ClapperboardIcon
              }].map(action => {
                const Icon = action.icon;
                return <div key={action.label} className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#0d1727] px-3 py-2.5" data-id="element-275">
                      <Icon className="h-3.5 w-3.5 text-gold" aria-hidden="true" data-id="element-276" />
                      <span className="font-montserrat text-[9px] text-[#dce3ed]" data-id="element-277">
                        {action.label}
                      </span>
                    </div>;
              })}
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-white/10 bg-[#0d1727] p-3" data-id="element-278">
              <div className="mb-3 flex items-center justify-between" data-id="element-279">
                <span className="font-montserrat text-[9px] font-medium text-[#dae2ee]" data-id="element-280">
                  Formato de entrega
                </span>
                <MaximizeIcon className="h-3.5 w-3.5 text-[#8f9db2]" aria-hidden="true" data-id="element-281" />
              </div>
              <div className="flex gap-2" data-id="element-282">
                {formats.map((format, index) => <span key={format} className={`flex-1 rounded-md py-2 text-center font-montserrat text-[9px] font-semibold ${index === 0 ? 'bg-gold text-richBlack' : 'border border-white/10 text-[#9eabc0]'}`} data-id="element-283">
                    {format}
                  </span>)}
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between" data-id="element-284">
              <div className="flex items-center gap-2 text-[#99a8bd]" data-id="element-285">
                <PlayIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-286" />
                <span className="font-montserrat text-[9px]" data-id="element-287">
                  Vista previa social
                </span>
              </div>
              <button type="button" className="inline-flex items-center gap-1.5 rounded-md bg-gold px-3 py-2 font-montserrat text-[9px] font-semibold text-richBlack" data-id="element-288">
                <DownloadIcon className="h-3 w-3" aria-hidden="true" data-id="element-289" />
                PNG · JPG · PDF
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}